import { Dialog, DialogActions, DialogContent, DialogTitle, Accordion, AccordionDetails, AccordionSummary, IconButton, makeStyles, Menu, MenuItem, styled, Toolbar as ToolbarBase } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SortIcon from '@material-ui/icons/Sort';
import { Alert, AlertTitle, TreeView } from '@material-ui/lab';
import TreeItem from '@material-ui/lab/TreeItem';
import { Linked, useIO, useLinked } from '@type-r/react';
import * as React from 'react';
import { FunctionComponent, ReactNode } from 'react';
import { jumpToAnchor, span } from '../common';
import { Loading } from '../flex';
import { Button, PrimaryButton } from './buttons';
import { ReadOnly } from './inputs';
import { LqxTitle, TitleWithQaId, useQaId } from './qa-ids';

export const Columns = styled('div')({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
})

export const Toolbar = styled( ToolbarBase )({
    paddingTop: 4,
    paddingBottom: 4,
    minHeight: '32px',
    backgroundColor: 'white'
})

export const AdvancedSearchBar =  styled( 'div' )({
  paddingTop: 4,
  paddingBottom: 4,
  textAlign: 'center'
})

export type SortOptions = { [ attr : string ] : string }

export interface SelectSortByProps {
    options : SortOptions;
    className? : string;
    fff? : string
    $attr : Linked<string>;
    $asc : Linked<boolean>;
}
/**
 * Select the sort field name and the sort order
 */
export const SelectSortBy = styled(
    ({ className, options, $attr, $asc } : SelectSortByProps ) => {
        const $menuAnchor = useLinked( null );

        return <>
            <span
                className={ className }
                onClick={ e => $menuAnchor.set( e.currentTarget ) }
            >
                Sort By: <u>{ options[ $attr.value ] }</u>
            </span>
            <Menu
                getContentAnchorEl={null}
                anchorEl={ $menuAnchor.value }
                open={ Boolean( $menuAnchor.value ) }
                onClose={ $menuAnchor.null }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                { Object.keys( options ).map( ( attr : string ) =>
                    <MenuItem key={attr}
                        onClick={ () => {
                            $menuAnchor.null();
                            $attr.set( attr )
                        }}
                    >
                        { options[ attr ] }
                    </MenuItem>
                )}
            </Menu>

            <IconButton aria-label="Sort By" size="small" onClick={ () => $asc.update( toggle )}>
                { $asc.value ? '↑' : '↓' } <SortIcon/>
            </IconButton>
        </>
    }
)({
    color: '#728A97',
    fontFamily: 'Montserrat',
    fontSize: 14,
    whiteSpace: 'nowrap',
    fontWeight: 500,
    alignSelf: 'center',
    letterSpacing: 'NaNpx',
    textAlign: 'center',
    cursor: 'pointer'
}) as FunctionComponent<SelectSortByProps>

const toggle = x => !x;

export const Column = styled('div')({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginRight: '1em',
    marginLeft: '1em'
})


export const TopSection = ({ id, title, children, 'qa-id' : lqxId } : 
    TitleWithQaId<{
    id? : string
    children : React.ReactNode
}>) => {
    const classes = CollapsableSection.useStyles();
    const nextId = useQaId( lqxId || title );
    return (
        <LqxTitle value={ nextId }>
            <div id={id} data-qa-id={nextId} className={classes.superSection}>
                <div className={classes.superSectionTitle}>{title}</div>
                <div>{children}</div>
            </div>
        </LqxTitle>
    )
}

/**
 * Main grouping block of the form layout.
 * (?) To be renamed to FieldSet (?)
 */
export const CollapsableSection = ({
    id, 
    title, 
    info,
    right,
    noMargins,
    'qa-id' : lqxId,
    notExpanded,
    titleClassName,    
    noBorder,
    noShadow,
    noTitleIndentation,
    children
} : TitleWithQaId<{
    id? : string,  
    info? : ReactNode,
    right? : ReactNode,
    noMargins? : boolean,
    notExpanded? : boolean,
    titleClassName? : string,
    noBorder? : boolean,
    noShadow?: boolean,
    noTitleIndentation?: boolean,
    children : ReactNode
}>) => {
    const classes = CollapsableSection.useStyles();
    const nextId = useQaId( lqxId || title );

    return (
        <LqxTitle value={ nextId }>
            <Accordion defaultExpanded={ !notExpanded }  id={ id } data-qa-id={ nextId } elevation={ noShadow && 0 } className={ noBorder && classes.hideBorder }>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={noTitleIndentation && classes.noTitleIndentation }
                >
                    <span className={titleClassName? titleClassName : classes.sectionTitle}>{title}</span>
                    <span className={classes.title}>{info || ""}</span>
                    { right && <span className={classes.right}>{right}</span> }
                </AccordionSummary>
                <AccordionDetails classes={{ root: noMargins ? classes.noMargins : classes.root }}>
                    {children}
                </AccordionDetails>
            </Accordion>
        </LqxTitle>
    )
}

export const SectionTitle = span({
    color: '#052C4C',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginRight: '1em',
    display: 'flex',
    alignItems: 'center'
});

CollapsableSection.useStyles = makeStyles({
    root: {
        //flexWrap: 'wrap',
        flexDirection: 'column',
        wordWrap: 'break-word'
    },

    noMargins : {
        flexWrap: 'wrap',
        flexDirection: 'column',
        paddingLeft : 0,
        paddingRight : 0,
    },

    right : {
        flex: '1 1 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },

    superSectionTitle: {
        color: '#052C4C',
        fontSize: 18,
        fontWeight: 500,
        marginBottom: 10
    },

    superSection: {
        marginBottom: 10,
        marginTop: 10
    },

    sectionTitle: {
        color: '#052C4C',
        fontSize: 14,
        fontWeight: 600,
        textTransform: 'uppercase',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    },

    title: {
        color: '#052C4C',
        fontSize: 14,
        display: "flex",
        alignItems: "center"
    },

    subsection: {
        //minWidth : '100%',
        marginTop: 4,
        marginBottom: 4
    },

    subsectionTitle: {
        color: '#668AA5',
        fontSize: 14,
        fontWeight: 600,
        textTransform: 'uppercase',
        marginBottom: 16
    },

    noTitleIndentation: {
        padding: 0
    },

    hideBorder: {
        '&.MuiAccordion-root:before': {
            display: 'none',
        },
    }
})

export const Section = ({ id, title, children, 'qa-id' : lqxId } : 
    TitleWithQaId<{
        id? : string
        children: ReactNode
    }> 
) => {
    const classes = CollapsableSection.useStyles();
    const nextId = useQaId( lqxId || title );

    return (
        <LqxTitle value={ nextId }>
            <div id={ id } className={classes.superSection} data-qa-id={nextId}>
                <SectionTitle>{title}</SectionTitle>
                <div>{children}</div>
            </div>
        </LqxTitle>

    )
}

export const Subsection = ({ id, title, children, 'qa-id' : lqxId } : 
    TitleWithQaId<{
        id? : string
        children: ReactNode
    }>
) => {
    const classes = CollapsableSection.useStyles();
    const nextId = useQaId( lqxId || title );

    return (
        <LqxTitle value={ nextId }>
            <div id={ id } className={classes.subsection} data-qa-id={nextId}>
                <div className={classes.subsectionTitle}>{title}</div>
                <div>
                    {children}
                </div>
            </div>
        </LqxTitle>
    )
}

/**
 * Navigation tree menue.
 */
export const NavTree = ({ children, defaultExpanded } : {
    /** DOM element ids of the groups to be expanded by default */
    defaultExpanded? : string[]
    children : React.ReactNode
}) => {
    const [expanded, setExpanded] = React.useState(defaultExpanded);

    const handleToggle = (event: React.ChangeEvent<HTMLElement>, nodeIds: string[]) => {
        if (event.target.closest(".MuiTreeItem-iconContainer")) {
            setExpanded(nodeIds);
        }
    };

    return (
        <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            expanded={expanded}
            onNodeToggle={handleToggle}
        >
            { children }
        </TreeView>
    );
};

const navTreeItemFontStyle = makeStyles({
    label: {
      fontSize: 16
    }
  });
export const NavTreeItem = ({ target, label, children, ...rest } :{
    /** display label */
    label : string

    /** id of the DOM element to navigate */
    target : string

    children? : JSX.Element[] | JSX.Element
}) => {
    const classes = navTreeItemFontStyle( rest );
    return (<TreeItem classes = { classes }
        nodeId={ target }
        label={ label }
        onClick={ () => jumpToAnchor(target) }
    >
        { children }
    </TreeItem>)
}

export const PopupDialog = ({ title, children, actions, hideTitle = false, ...rest }) =>
    <Dialog
        PaperProps={ { "aria-label": `${title}` }}
        open={ true }
        fullWidth
        { ...rest }
    >
        {hideTitle ? undefined : (
        <DialogTitle>
            <SectionTitle>{ title }</SectionTitle>
        </DialogTitle> )}
        <DialogContent>
            <ReadOnly.Provider value={false}>
                { children }
            </ReadOnly.Provider>
        </DialogContent>
        <DialogActions>
            { actions }
        </DialogActions>
    </Dialog>
//<ReadOnly.Provide value={false}>
export const PopupIODialog = ({ title, width, formatError, actionLabel, success, $isOpen, doIO, children, disabled, hideTitleOnSuccess, onSuccess, classes, maxWidth, altActionLabel, altDoIO } : {
    title : string,
    actionLabel : React.ReactNode,
    success : any,
    formatError? : ( err : Error ) => React.ReactNode,
    width? : any,
    onSuccess? : () => void,
    $isOpen : Linked<boolean>,
    doIO : () => Promise<any>,
    children? : any,
    disabled? : boolean,
    hideTitleOnSuccess? : boolean,
    classes? : any,
    maxWidth? : false | 'lg' | 'md' | 'sm' | 'xl' | 'xs',
    altActionLabel? : React.ReactNode,
    altDoIO? : () => Promise<any>
}) => {
    const $doIO = useLinked( null );
 
    const isReady = useIO(
        async () => {
            if($doIO.value === 'primary') {
                return await doIO();
            }
            else if($doIO.value === 'alt') {
                return altDoIO && await altDoIO()
            }
            return false;
        }, [ $doIO.value ]
    )
 
    const [hideTitle, setHideTitle] = React.useState(false);
 
    React.useEffect(() => {
        if (hideTitleOnSuccess && $doIO.isTruthy && isReady) {
            setHideTitle(true);
        }
    },
    [ $doIO.value, isReady['_changeToken'] ]);
 
    const error = isReady && typeof isReady === 'object' ? isReady as Error : null;
    return (
        <PopupDialog
            title={ title }
            width={width}
            actions={
                $doIO.isTruthy ?
                    <PrimaryButton disabled={!isReady} onClick={ () => {
                        onSuccess && onSuccess();
                        $isOpen.false();
                    }}>
                        Close
                    </PrimaryButton>
                :
                    <>
                        <Button onClick={ $isOpen.false }>
                            Cancel
                        </Button>
                        { altActionLabel && 
                            <Button qa-id='alt-action' disabled={ disabled } onClick={ () => $doIO.set('alt')}>
                                { altActionLabel }
                            </Button>
                        }
                        <PrimaryButton disabled={ disabled } onClick={ () => $doIO.set('primary') }>
                            { actionLabel }
                        </PrimaryButton>
                    </>
            }
            onClose={ $isOpen.false }
            hideTitle={ hideTitle }
            classes={classes}
            maxWidth={maxWidth}
        >
            { isReady ? (
                error ?
                    <Alert severity="error">
                        <AlertTitle>
                            Error
                        </AlertTitle>
                        { formatError ? formatError( error ) : error.message }
                    </Alert>
                :
                $doIO.isTruthy ?
                    success :
                    children
            ) :
                <Loading/>
            }
        </PopupDialog>
    )
}